interface AppApiConstants {
  defaultPageSize: number;
  /**
   * Maximum serialized URL length for tRPC batches. Encoded query inputs are ASCII.
   * Use the ALB's 16 KiB request-line limit minus HTTP/1.1 framing; a lower arbitrary
   * cap rejects supported singleton report filters before they can be sent.
   * tRPC counts the origin outside the browser, making that case more conservative.
   * Headers have separate limits; this is not a measured production header allowance.
   * tRPC splits between calls; an individual query must still fit this URL budget.
   * @see https://docs.aws.amazon.com/elasticloadbalancing/latest/application/load-balancer-limits.html
   */
  maxBatchUrlLength: number;
}

interface AppDBConstants {
  /**
   * How long a connection should wait for a connection to be available
   * @note in milliseconds
   * @default 2000
   */
  transactionMaxWait: number;
  /**
   * How long an interactive transaction should run before timing out
   * and rolling back
   * @note in milliseconds
   * @default 5000
   */
  transactionTimeout: number;
  /**
   * How long Prisma waits to take a connection from the pool before failing a query.
   *
   * This is Prisma's own `pool_timeout` default, restated here so application code can refer to the
   * budget an ordinary query already gets. It is not a setting: nothing in this repo passes
   * `pool_timeout` in `DATABASE_URL`, so the default is what applies.
   *
   * Used as the `maxWait` for a transaction that is not on a request path, so such a transaction waits
   * for a connection at least as long as a plain query would instead of giving up first.
   *
   * CONDITION, not a guarantee, and the margin is zero: this equals Prisma's default rather than
   * exceeding it, so the two budgets are tied, not ordered. What that buys is the band in between —
   * a connection that arrives after the old 5s but before the pool gives up is now waited for. What it
   * does not buy is a promise about which error wins under total starvation, where a query and a
   * transaction give up at the same moment. Setting a larger `pool_timeout` in `DATABASE_URL` without
   * raising this reopens the original gap, with transactions failing early in a band where queries
   * still succeed.
   * @note in milliseconds
   */
  poolAcquisitionTimeout: number;
  /**
   * Longer budget for bill create/update/open interactive transactions (WO cost cascade).
   * @note in milliseconds
   */
  billInteractiveTransactionTimeout: number;
  /**
   * Budget for exchange-rate period creation, which now runs the period insert and the entire
   * due-reversal batch inside one interactive transaction (all-or-nothing). Sized above the
   * default transaction timeout so a realistic overdue-reversal backlog can post without tripping
   * the timeout and failing the period create.
   * @note in milliseconds
   */
  periodCreateInteractiveTransactionTimeout: number;
  /**
   * Budget for AR/AP payment-reversal transactions that also run the SC-2086 settlement
   * true-up guard (findAndVoidBlockingSettlementTrueUp): entry + period row locks, a
   * correcting GL post, and mapping inserts per linked entry, on top of the reversal's own
   * work. Sized above the default so real lock contention (e.g. two concurrent multi-period
   * reversals) surfaces a mapped domain error instead of a generic Prisma timeout. Value
   * matches periodCreateInteractiveTransactionTimeout above (same rationale: a heavy
   * multi-lock FX operation) rather than a load-tested figure — no incident/staging data
   * motivates this specific number, it is a conservative match to the one existing precedent
   * for comparable transactions in this module.
   * @note in milliseconds
   */
  settlementTrueUpGuardTransactionTimeout: number;
  /**
   * SC-2175 — budget for a single exchange-rate revaluation POST transaction (recompute the plan under the
   * period lock, aggregate + post GL by account/date, batch the source→GL mappings, flip the run to POSTED).
   * Bounds the previously-unbounded post txn: exceeding it aborts the whole post (fail-closed, run stays
   * PREVIEW) and surfaces REVALUATION_POST_TOO_LARGE — an observable escalation signal that measured source
   * volume may warrant a leased background job. Matches the other heavy-FX-transaction budgets in this module
   * (conservative match, not a load-tested figure).
   * @note in milliseconds
   */
  revaluationPostInteractiveTransactionTimeout: number;
}

interface AppCSVConstants {
  streamEncoding: BufferEncoding;
  streamPassthroughHighWatermark: number;
  streamQueueSize: number;
  streamSize: number;
}

interface AppConstants {
  api: AppApiConstants;
  db: AppDBConstants;
  csv: AppCSVConstants;
  sandboxUrl: string;
  sandboxSupportUrl: string;
}

export const appConstants: AppConstants = {
  api: {
    defaultPageSize: 50,
    // Reserve method, two spaces, HTTP version and CRLF; POST is longer than GET.
    maxBatchUrlLength: 16 * 1024 - "POST  HTTP/1.1\r\n".length,
  },
  db: {
    transactionMaxWait: 5000,
    transactionTimeout: 30000, // 30 seconds - increased from 10s for complex operations
    poolAcquisitionTimeout: 10000, // Prisma's `pool_timeout` default; this repo sets no override
    billInteractiveTransactionTimeout: 60000,
    periodCreateInteractiveTransactionTimeout: 60000,
    settlementTrueUpGuardTransactionTimeout: 60000,
    revaluationPostInteractiveTransactionTimeout: 60000,
  },
  csv: {
    streamEncoding: "utf8",
    streamPassthroughHighWatermark: 64 * 1024,
    streamQueueSize: 2,
    streamSize: 8 * 1024 * 1024,
  },
  sandboxUrl: "https://sandbox.shelfcycle.com",
  sandboxSupportUrl: "https://shelfcycle.crunch.help/en/administration/using-sandbox-accounts-for-testing",
};
